import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import ScrollToTop from "@/components/ScrollToTop";
import PageLoadingFallback from "@/components/PageLoadingFallback";
import { SentryErrorBoundary, PageErrorBoundary } from "@/components/error-boundary";
import { getPreferredLocale } from "@/utils/languageDetection";

// Swedish pages (new structure)
import IndexSv from "./pages/sv/Index";
import NotFoundSv from "./pages/sv/others/NotFound";

// English pages (new structure)
import IndexEn from "./pages/en/Index";
import NotFoundEn from "./pages/en/others/NotFound";

// Shared pages (no locale)
import LovableTest from "./pages/LovableTest";
import LovableDebug from "./pages/LovableDebug";

// Special pages (not part of main translation - utility/debug pages)
const Ecosystem = lazy(() => import("./pages/others/Ecosystem"));
const AIAssistant = lazy(() => import("./pages/others/AIAssistant"));
const Design = lazy(() => import("./pages/others/Design"));

// Swedish sector pages
const ScaleupsSv = lazy(() => import("./pages/sv/Scaleups"));
const StartupsSv = lazy(() => import("./pages/sv/Startups"));
const StartupJourneySv = lazy(() => import("./pages/sv/StartupJourney"));

// Swedish about pages
const AboutSv = lazy(() => import("./pages/sv/about/About"));
const NewsroomSv = lazy(() => import("./pages/sv/about/Newsroom"));
const PartnersSv = lazy(() => import("./pages/sv/about/Partners"));

// Swedish reports pages
const ReportsSv = lazy(() => import("./pages/sv/reports/Reports"));
const ReportDetailSv = lazy(() => import("./pages/sv/reports/ReportDetail"));

// Swedish Insight Lab pages (renamed from thinktank)
const WhatWeDoSv = lazy(() => import("./pages/sv/insightlab/WhatWeDo"));
const ConsultationSv = lazy(() => import("./pages/sv/insightlab/Consultation"));
const ResearchSv = lazy(() => import("./pages/sv/insightlab/Research"));
const ContributeSv = lazy(() => import("./pages/sv/insightlab/Contribute"));
const CalendarSv = lazy(() => import("./pages/sv/insightlab/Calendar"));

// Swedish Engage pages
const StartupEngageSv = lazy(() => import("./pages/sv/engage/StartupEngage"));
const DataEngageSv = lazy(() => import("./pages/sv/engage/DataEngage"));
const PartnerEngageSv = lazy(() => import("./pages/sv/engage/PartnerEngage"));

// Swedish Sector pages
const DeeptechSectorSv = lazy(() => import("./pages/sv/sector/Deeptech"));
const LifeScienceSectorSv = lazy(() => import("./pages/sv/sector/LifeScience"));
const ClimateSectorSv = lazy(() => import("./pages/sv/sector/Climate"));
const AISectorSv = lazy(() => import("./pages/sv/sector/AI"));

// Swedish Footer pages
const PrivacyPolicySv = lazy(() => import("./pages/sv/footer/PrivacyPolicy"));
const TermsOfUseSv = lazy(() => import("./pages/sv/footer/TermsOfUse"));
const CookiePolicySv = lazy(() => import("./pages/sv/footer/CookiePolicy"));
const AccessibilitySv = lazy(() => import("./pages/sv/footer/Accessibility"));

// English sector pages
const ScaleupsEn = lazy(() => import("./pages/en/Scaleups"));
const StartupsEn = lazy(() => import("./pages/en/Startups"));
const DeeptechSectorEn = lazy(() => import("./pages/en/sector/Deeptech"));
const LifeScienceSectorEn = lazy(() => import("./pages/en/sector/LifeScience"));
const ClimateSectorEn = lazy(() => import("./pages/en/sector/Climate"));
const AISectorEn = lazy(() => import("./pages/en/sector/AI"));

const StartupJourneyEn = lazy(() => import("./pages/en/StartupJourney"));
// English about pages
const AboutEn = lazy(() => import("./pages/en/about/About"));
const NewsroomEn = lazy(() => import("./pages/en/about/Newsroom"));
const PartnersEn = lazy(() => import("./pages/en/about/Partners"));

// English reports pages
const ReportsEn = lazy(() => import("./pages/en/reports/Reports"));
const ReportDetailEn = lazy(() => import("./pages/en/reports/ReportDetail"));

// English Insight Lab pages (renamed from thinktank)
const WhatWeDoEn = lazy(() => import("./pages/en/insightlab/WhatWeDo"));
const ConsultationEn = lazy(() => import("./pages/en/insightlab/Consultation"));
const ResearchEn = lazy(() => import("./pages/en/insightlab/Research"));
const ContributeEn = lazy(() => import("./pages/en/insightlab/Contribute"));
const CalendarEn = lazy(() => import("./pages/en/insightlab/Calendar"));

// English Engage pages
const StartupEngageEn = lazy(() => import("./pages/en/engage/StartupEngage"));
const DataEngageEn = lazy(() => import("./pages/en/engage/DataEngage"));
const PartnerEngageEn = lazy(() => import("./pages/en/engage/PartnerEngage"));

// English Footer pages
const PrivacyPolicyEn = lazy(() => import("./pages/en/footer/PrivacyPolicy"));
const TermsOfUseEn = lazy(() => import("./pages/en/footer/TermsOfUse"));
const CookiePolicyEn = lazy(() => import("./pages/en/footer/CookiePolicy"));
const AccessibilityEn = lazy(() => import("./pages/en/footer/Accessibility"));

// Swedish Focus Area pages (from main, moved to /sv/)
const BiotechnologySv = lazy(() => import("./pages/sv/focus-areas/BiotechnologyPage"));
const DigitalTechnologySv = lazy(() => import("./pages/sv/focus-areas/DigitalTechnologyPage"));
const EnergySv = lazy(() => import("./pages/sv/focus-areas/EnergyPage"));
const MaterialsSv = lazy(() => import("./pages/sv/focus-areas/MaterialsPage"));
const QuantumSv = lazy(() => import("./pages/sv/focus-areas/QuantumPage"));

// TODO: Create English Focus Area pages
// const BiotechnologyEn = lazy(() => import("./pages/en/focus-areas/BiotechnologyPage"));
// const DigitalTechnologyEn = lazy(() => import("./pages/en/focus-areas/DigitalTechnologyPage"));
// const EnergyEn = lazy(() => import("./pages/en/focus-areas/EnergyPage"));
// const MaterialsEn = lazy(() => import("./pages/en/focus-areas/MaterialsPage"));
// const QuantumEn = lazy(() => import("./pages/en/focus-areas/QuantumPage"));


const queryClient = new QueryClient();

/**
 * Locale Redirect Component
 * Redirects to appropriate locale based on browser language or localStorage
 * No Context API dependency - uses language detection utilities
 */
const LocaleRedirect = ({ to }: { to: string }) => {
  const locale = getPreferredLocale();
  const targetPath = to ? `/${locale}${to}` : `/${locale}/`;
  return <Navigate to={targetPath} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <SentryErrorBoundary>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Root redirect to locale-prefixed home */}
            <Route path="/" element={<LocaleRedirect to="" />} />

            {/* Lovable test routes - NO locale prefix */}
            <Route path="/lovable-test" element={<LovableTest />} />
            <Route path="/lovable-debug" element={<LovableDebug />} />

            {/* Swedish routes */}
            <Route path="/sv">
              <Route index element={<IndexSv />} />
              <Route path="startups" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><StartupsSv /></PageErrorBoundary></Suspense>} />
              <Route path="startup-journey" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><StartupJourneySv /></PageErrorBoundary></Suspense>} />
              <Route path="scaleups" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ScaleupsSv /></PageErrorBoundary></Suspense>} />
              <Route path="about" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><AboutSv /></PageErrorBoundary></Suspense>} />
              <Route path="newsroom" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><NewsroomSv /></PageErrorBoundary></Suspense>} />
              <Route path="partners" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><PartnersSv /></PageErrorBoundary></Suspense>} />
              <Route path="reports" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ReportsSv /></PageErrorBoundary></Suspense>} />
              <Route path="reports/:id" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ReportDetailSv /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/what-we-do" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><WhatWeDoSv /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/consultation" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ConsultationSv /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/research" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ResearchSv /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/contribute" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ContributeSv /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/calendar" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><CalendarSv /></PageErrorBoundary></Suspense>} />
              <Route path="calendar" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><CalendarSv /></PageErrorBoundary></Suspense>} />
              <Route path="engage/startup" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><StartupEngageSv /></PageErrorBoundary></Suspense>} />
              <Route path="engage/data" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><DataEngageSv /></PageErrorBoundary></Suspense>} />
              <Route path="engage/partner" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><PartnerEngageSv /></PageErrorBoundary></Suspense>} />
              <Route path="sector/deeptech" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><DeeptechSectorSv /></PageErrorBoundary></Suspense>} />
              <Route path="sector/life-science" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><LifeScienceSectorSv /></PageErrorBoundary></Suspense>} />
              <Route path="sector/climate" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ClimateSectorSv /></PageErrorBoundary></Suspense>} />
              <Route path="sector/ai" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><AISectorSv /></PageErrorBoundary></Suspense>} />
              <Route path="focus-areas/biotechnology" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><BiotechnologySv /></PageErrorBoundary></Suspense>} />
              <Route path="focus-areas/digital-technology" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><DigitalTechnologySv /></PageErrorBoundary></Suspense>} />
              <Route path="focus-areas/energy" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><EnergySv /></PageErrorBoundary></Suspense>} />
              <Route path="focus-areas/materials" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><MaterialsSv /></PageErrorBoundary></Suspense>} />
              <Route path="focus-areas/quantum" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><QuantumSv /></PageErrorBoundary></Suspense>} />
              <Route path="privacy-policy" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><PrivacyPolicySv /></PageErrorBoundary></Suspense>} />
              <Route path="privacy" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><PrivacyPolicySv /></PageErrorBoundary></Suspense>} />
              <Route path="terms" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><TermsOfUseSv /></PageErrorBoundary></Suspense>} />
              <Route path="cookies" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><CookiePolicySv /></PageErrorBoundary></Suspense>} />
              <Route path="accessibility" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><AccessibilitySv /></PageErrorBoundary></Suspense>} />
              <Route path="404" element={<NotFoundSv />} />
              <Route path="*" element={<NotFoundSv />} />
            </Route>

            {/* English routes */}
            <Route path="/en">
              <Route index element={<IndexEn />} />
              <Route path="startups" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><StartupsEn /></PageErrorBoundary></Suspense>} />
              <Route path="scaleups" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ScaleupsEn /></PageErrorBoundary></Suspense>} />
              <Route path="startup-journey" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><StartupJourneyEn /></PageErrorBoundary></Suspense>} />
              <Route path="about" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><AboutEn /></PageErrorBoundary></Suspense>} />
              <Route path="newsroom" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><NewsroomEn /></PageErrorBoundary></Suspense>} />
              <Route path="partners" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><PartnersEn /></PageErrorBoundary></Suspense>} />
              <Route path="reports" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ReportsEn /></PageErrorBoundary></Suspense>} />
              <Route path="reports/:id" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ReportDetailEn /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/what-we-do" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><WhatWeDoEn /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/consultation" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ConsultationEn /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/research" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ResearchEn /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/contribute" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ContributeEn /></PageErrorBoundary></Suspense>} />
              <Route path="insightlab/calendar" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><CalendarEn /></PageErrorBoundary></Suspense>} />
              <Route path="calendar" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><CalendarEn /></PageErrorBoundary></Suspense>} />
              <Route path="engage/startup" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><StartupEngageEn /></PageErrorBoundary></Suspense>} />
              <Route path="engage/data" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><DataEngageEn /></PageErrorBoundary></Suspense>} />
              <Route path="engage/partner" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><PartnerEngageEn /></PageErrorBoundary></Suspense>} />
              <Route path="sector/deeptech" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><DeeptechSectorEn /></PageErrorBoundary></Suspense>} />
              <Route path="sector/life-science" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><LifeScienceSectorEn /></PageErrorBoundary></Suspense>} />
              <Route path="sector/climate" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><ClimateSectorEn /></PageErrorBoundary></Suspense>} />
              <Route path="sector/ai" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><AISectorEn /></PageErrorBoundary></Suspense>} />
              {/* TODO: Uncomment when English focus area pages are created */}
              {/* <Route path="focus-areas/biotechnology" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><BiotechnologyEn /></PageErrorBoundary></Suspense>} /> */}
              {/* <Route path="focus-areas/digital-technology" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><DigitalTechnologyEn /></PageErrorBoundary></Suspense>} /> */}
              {/* <Route path="focus-areas/energy" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><EnergyEn /></PageErrorBoundary></Suspense>} /> */}
              {/* <Route path="focus-areas/materials" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><MaterialsEn /></PageErrorBoundary></Suspense>} /> */}
              {/* <Route path="focus-areas/quantum" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><QuantumEn /></PageErrorBoundary></Suspense>} /> */}
              <Route path="privacy-policy" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><PrivacyPolicyEn /></PageErrorBoundary></Suspense>} />
              <Route path="privacy" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><PrivacyPolicyEn /></PageErrorBoundary></Suspense>} />
              <Route path="terms" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><TermsOfUseEn /></PageErrorBoundary></Suspense>} />
              <Route path="cookies" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><CookiePolicyEn /></PageErrorBoundary></Suspense>} />
              <Route path="accessibility" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><AccessibilityEn /></PageErrorBoundary></Suspense>} />
              <Route path="404" element={<NotFoundEn />} />
              <Route path="*" element={<NotFoundEn />} />
            </Route>

            {/* Special utility pages (no locale prefix) */}
            <Route path="/ecosystem" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><Ecosystem /></PageErrorBoundary></Suspense>} />
            <Route path="/ai-assistant" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><AIAssistant /></PageErrorBoundary></Suspense>} />
            <Route path="/design" element={<Suspense fallback={<PageLoadingFallback />}><PageErrorBoundary><Design /></PageErrorBoundary></Suspense>} />

            {/* Legacy URL redirects (without locale prefix) - redirect to localized versions */}
            <Route path="/about" element={<LocaleRedirect to="/about" />} />
            <Route path="/reports" element={<LocaleRedirect to="/reports" />} />
            <Route path="/reports/:id" element={<LocaleRedirect to="/reports/:id" />} />
            <Route path="/startups" element={<LocaleRedirect to="/startups" />} />
            <Route path="/scaleups" element={<LocaleRedirect to="/scaleups" />} />
            <Route path="/startup-journey" element={<LocaleRedirect to="/startup-journey" />} />
            <Route path="/partners" element={<LocaleRedirect to="/partners" />} />
            <Route path="/newsroom" element={<LocaleRedirect to="/newsroom" />} />
            <Route path="/calendar" element={<LocaleRedirect to="/calendar" />} />
            <Route path="/insightlab/*" element={<LocaleRedirect to="/insightlab" />} />
            <Route path="/engage/*" element={<LocaleRedirect to="/engage" />} />
            <Route path="/sector/*" element={<LocaleRedirect to="/sector" />} />
            <Route path="/focus-areas/*" element={<LocaleRedirect to="/focus-areas" />} />
            <Route path="/privacy" element={<LocaleRedirect to="/privacy" />} />
            <Route path="/privacy-policy" element={<LocaleRedirect to="/privacy-policy" />} />
            <Route path="/terms" element={<LocaleRedirect to="/terms" />} />
            <Route path="/cookies" element={<LocaleRedirect to="/cookies" />} />
            <Route path="/accessibility" element={<LocaleRedirect to="/accessibility" />} />

            {/* Catch-all: redirect to 404 with locale */}
            <Route path="*" element={<LocaleRedirect to="/404" />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </SentryErrorBoundary>
  </QueryClientProvider>
);

export default App;
